// Shared configuration for the translated versions of the scenario.
// Translations come from https://github.com/tlon-team/cassandra-nextjs
// (internationalization branch); see /translations/<locale>/.

export type LocaleCode = 'es' | 'fr' | 'it';

export const LOCALES: { code: LocaleCode; label: string; short: string }[] = [
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'it', label: 'Italiano', short: 'IT' },
];

// Base paths (without locale prefix) that have translated equivalents.
export const TRANSLATED_PATHS = ['', '/race', '/slowdown', '/summary'];

// hreflang alternates for a translated base path, for page metadata.
export function localeAlternates(basePath: string) {
  return {
    languages: {
      en: basePath || '/',
      ...Object.fromEntries(LOCALES.map((l) => [l.code, `/${l.code}${basePath}`])),
      'x-default': basePath || '/',
    },
  };
}

export interface LocaleStrings {
  narrativeTitles: string[];
  endingTitles: string[];
  chooseEnding: { heading: string; slowdown: string; race: string };
  readOtherEnding: string;
  watchVideoSummary: string;
  summaryTitle: string;
  headerSubtitle: { date: string; listen: string; pause: string; watch: string };
}

export const localeStrings: Record<LocaleCode, LocaleStrings> = {
  es: {
    narrativeTitles: ["Prólogo", "2025", "2026", "Ene 2027", "Feb 2027", "Mar 2027", "Abr 2027", "May 2027", "Jun 2027", "Jul 2027", "Ago 2027", "Sep 2027", "Oct 2027", "Nov 2027", "Dic 2027", "2028+"],
    endingTitles: ["Abr y May", "Jun y Jul", "Ago y Sep", "Oct", "Nov", "Dic", "2028+"],
    chooseEnding: { heading: "Elige tu final", slowdown: "Desaceleración", race: "Carrera" },
    readOtherEnding: "Lee el otro final",
    watchVideoSummary: "Ver el resumen en video",
    summaryTitle: "Resumen — AI 2027",
    headerSubtitle: { date: "3 de abril de 2025", listen: "Escuchar", pause: "Pausar", watch: "Ver" },
  },
  fr: {
    narrativeTitles: ["Avant-propos", "2025", "2026", "Janv 2027", "Févr 2027", "Mars 2027", "Avr 2027", "Mai 2027", "Juin 2027", "Juil 2027", "Août 2027", "Sept 2027", "Oct 2027", "Nov 2027", "Déc 2027", "2028+"],
    endingTitles: ["Avr et Mai", "Juin et Juil", "Août et Sept", "Oct", "Nov", "Déc", "2028+"],
    chooseEnding: { heading: "Choisissez votre fin", slowdown: "Ralentissement", race: "Course" },
    readOtherEnding: "Lire l'autre fin",
    watchVideoSummary: "Voir le résumé en vidéo",
    summaryTitle: "Résumé — AI 2027",
    headerSubtitle: { date: "3 avril 2025", listen: "Écouter", pause: "Pause", watch: "Regarder" },
  },
  it: {
    narrativeTitles: ["Prefazione", "2025", "2026", "Gen 2027", "Feb 2027", "Mar 2027", "Apr 2027", "Mag 2027", "Giu 2027", "Lug 2027", "Ago 2027", "Set 2027", "Ott 2027", "Nov 2027", "Dic 2027", "2028+"],
    endingTitles: ["Apr e Mag", "Giu e Lug", "Ago e Set", "Ott", "Nov", "Dic", "2028+"],
    chooseEnding: { heading: "Scegli il tuo finale", slowdown: "Rallentamento", race: "Corsa" },
    readOtherEnding: "Leggi l'altro finale",
    watchVideoSummary: "Guarda il riepilogo in video",
    summaryTitle: "Riepilogo — AI 2027",
    headerSubtitle: { date: "3 aprile 2025", listen: "Ascolta", pause: "Pausa", watch: "Guarda" },
  },
};

// Narration audio per language, keyed the way the URL is: '' is English.
// English still lives on UploadThing (app ID ghjyjqzwz4); the translated
// recordings are served from /public/audio so that adding a language needs
// no third-party account or non-obvious credentials.
export const NARRATION_AUDIO: Record<'' | LocaleCode, string> = {
  '': 'https://ghjyjqzwz4.ufs.sh/f/9qHa0cBclQ7sd8I4JOvWfxDbCA5lLh9PFsygwTqB3HveZ1jd',
  es: '/audio/ai-2027-es-short.mp3',
  fr: '/audio/ai-2027-fr-short.mp3',
  it: '/audio/ai-2027-it-short.mp3',
};
